'use client'

import { ImageNotFound } from 'fukku/Image'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { PRODUCT_STATUS } from 'my-purchases/constants/productStatus'
import type { OrderLine } from 'my-purchases/types/purchaseList'
import { useMemo } from 'react'
import { cx } from 'utils/cx'
import SmartCropImage from 'utils/image/components/SmartCropImage/SmartCropImage'
import { ClientLink } from 'utils/link/client'

import { Tag } from 'fukku/Tag/Tag'
import styles from './ArticleImage.module.scss'
interface ArticleImageProps {
	readonly purchaseUrl: string
	readonly article: OrderLine
	readonly sizes: string
	readonly dataTestId: string
	readonly onClick: () => void
}

export function ArticleImage({
	purchaseUrl,
	article,
	sizes,
	dataTestId,
	onClick,
}: ArticleImageProps) {
	const { t } = useLabels()

	const { id, image, name, status } = article

	const isDisabled = status !== PRODUCT_STATUS.AVAILABLE

	const statusTag = useMemo(() => {
		if (
			[PRODUCT_STATUS.RETURNED, PRODUCT_STATUS.RETURN_REQUESTED].includes(
				status
			)
		) {
			return 'myPurchases.returned.tag'
		}

		if (status === PRODUCT_STATUS.OUT_OF_STOCK) {
			return 'myPurchases.noStock.tag'
		}

		return null
	}, [status])

	return (
		<div
			aria-hidden
			className={cx(styles.wrapper, isDisabled && styles.disabled)}
			data-testid={`${dataTestId}.image`}
		>
			<ClientLink
				href={purchaseUrl}
				linkProps={{ tabIndex: -1 }}
				onClick={onClick}
			>
				{image ? (
					<SmartCropImage src={image} alt={name} sizes={sizes} />
				) : (
					<ImageNotFound
						className={styles.wrapper}
						dataTestId={`myPurchases.card.${id}.image.notfound`}
					/>
				)}
			</ClientLink>

			{isDisabled && statusTag && (
				<Tag label={t(statusTag)} className={styles.tag} variant='floating' />
			)}
		</div>
	)
}
